"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { goalElements, referenceRangeElements } from "@/components/measurement-charts/chart-annotation-elements";
import MeasurementSingleChart from "@/components/measurement-charts/measurement-single-chart";
import ChartWrapper from "@/components/ui/charts/chart-wrapper";
import { useTimezone } from "@/hooks/use-timezone";
import { type AggregationFieldInfo, type MeasurementAggregationRule, type MeasurementBucketGranularity, aggregateBucketValues, formatBucketLabel, groupValuesByBucket } from "@/lib/measurement-aggregation";
import { hashToColor } from "@/lib/measurement-calculations";
import { sortedDates } from "@/lib/measurement-utils";
import type { MeasurementDataType, MeasurementGoal, MeasurementValueSummary, ReferenceRange } from "@/types/measurement";
interface MeasurementTrendChartViewProps {
  values: MeasurementValueSummary[];
  fieldId: string;
  fieldName: string;
  unit?: string | null;
  color?: string;
  referenceRanges?: ReferenceRange[];
  goal?: MeasurementGoal | null;
  /** Only numeric fields get a trend chart, so FLOAT is the safe default. */
  dataType?: MeasurementDataType;
  decimalPlaces?: number | null;
  granularity?: MeasurementBucketGranularity;
  rule?: MeasurementAggregationRule;
  isLoading?: boolean;
  isError?: boolean;
}
export default function MeasurementTrendChartView(t0) {
  const $ = _c(34);
  const {
    values,
    fieldId,
    fieldName,
    unit,
    color,
    referenceRanges,
    goal,
    dataType: t1,
    decimalPlaces: t2,
    granularity: t3,
    rule: t4,
    isLoading: t5,
    isError: t6
  } = t0;
  const dataType = t1 === undefined ? "FLOAT" : t1;
  const decimalPlaces = t2 === undefined ? null : t2;
  const granularity = t3 === undefined ? "DAY" : t3;
  const rule = t4 === undefined ? "LATEST" : t4;
  const isLoading = t5 === undefined ? false : t5;
  const isError = t6 === undefined ? false : t6;
  let t7;
  if ($[0] !== color || $[1] !== fieldId) {
    t7 = color || hashToColor(fieldId);
    $[0] = color;
    $[1] = fieldId;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const chartColor = t7;
  const timezone = useTimezone();
  let t8;
  if ($[3] !== dataType || $[4] !== decimalPlaces || $[5] !== fieldId || $[6] !== granularity || $[7] !== referenceRanges || $[8] !== rule || $[9] !== timezone || $[10] !== values) {
    const fieldMap = new Map([[fieldId, {
      dataType,
      decimalPlaces,
      referenceRanges
    }]]);
    const bucketed = aggregateBucketValues(groupValuesByBucket(values, granularity, timezone), fieldMap, rule);
    const dates = sortedDates(bucketed).reverse();
    t8 = dates.flatMap(date => bucketed[date].filter(_temp).map(v_0 => ({
      date,
      value: v_0.numericValue,
      isUserReported: Boolean(v_0.isUserReported)
    })));
    $[3] = dataType;
    $[4] = decimalPlaces;
    $[5] = fieldId;
    $[6] = granularity;
    $[7] = referenceRanges;
    $[8] = rule;
    $[9] = timezone;
    $[10] = values;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const chartData = t8;
  let t9;
  if ($[12] !== goal || $[13] !== referenceRanges) {
    t9 = [...referenceRangeElements(referenceRanges), ...goalElements(goal)];
    $[12] = goal;
    $[13] = referenceRanges;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const annotations = t9;
  const hasEnoughData = chartData.length >= 2;
  const t10 = `Failed to load ${fieldName} trend data`;
  let t11;
  if ($[15] !== granularity || $[16] !== timezone) {
    t11 = (date_0, style) => formatBucketLabel(date_0, granularity, timezone, style);
    $[15] = granularity;
    $[16] = timezone;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== referenceRanges || $[19] !== t11) {
    t12 = {
      referenceRanges,
      yAxisWidth: 64,
      formatDate: t11
    };
    $[18] = referenceRanges;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== annotations || $[22] !== chartColor || $[23] !== chartData || $[24] !== fieldName || $[25] !== t12 || $[26] !== unit) {
    t13 = <div className="h-48"><MeasurementSingleChart data={chartData} fieldName={fieldName} color={chartColor} unit={unit} annotations={annotations} cartesianOpts={t12} /></div>;
    $[21] = annotations;
    $[22] = chartColor;
    $[23] = chartData;
    $[24] = fieldName;
    $[25] = t12;
    $[26] = unit;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== hasEnoughData || $[29] !== isError || $[30] !== isLoading || $[31] !== t10 || $[32] !== t13) {
    t14 = <ChartWrapper isLoading={isLoading} isDataLoaded={hasEnoughData} hasFailed={isError} loadingClassName="h-48" errorMessage={t10}>{t13}</ChartWrapper>;
    $[28] = hasEnoughData;
    $[29] = isError;
    $[30] = isLoading;
    $[31] = t10;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function _temp(v) {
  return v.numericValue != null;
}